import React from "react";
import { motion, useMotionValue, useTransform, useMotionValueEvent } from "framer-motion";
import { HeroArt, TrackBadge, StageChip, TagChip, AvatarStack } from "./bits";
import { WinnerBadge } from "./winners/WinnerBadge";
import { PlayCircle } from "lucide-react";
import { cardTags } from "../lib/tagMeta";
import { SUPPORT_ENABLED } from "../lib/features";
import { haptic } from "../lib/confetti";

const COMMIT_X = 0.3;
const COMMIT_Y = 0.22;
const VEL = 550;

const exitVariant = (dir) => {
  const w = window.innerWidth * 1.4;
  const h = window.innerHeight * 1.1;
  if (dir === "left") return { x: -w, rotate: -22, opacity: 0, transition: { duration: 0.28, ease: [0.4, 0, 1, 1] } };
  if (dir === "right") return { x: w, rotate: 22, opacity: 0, transition: { duration: 0.32, ease: [0.4, 0, 1, 1] } };
  if (dir === "up") return { y: -h, opacity: 0, transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] } };
  return { opacity: 0 };
};

export default function SwipeCard({ project, isTop, offsetIndex, onCommit, showHint, reduced, directionRef }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-15, 15]);
  const leftOpacity = useTransform(x, [-140, -40], [1, 0]);
  const rightOpacity = useTransform(x, [40, 140], [0, 1]);
  const upOpacity = useTransform(y, [-140, -40], [1, 0]);

  const crossed = React.useRef(null);
  useMotionValueEvent(x, "change", (v) => {
    const w = window.innerWidth;
    if (Math.abs(v) > w * COMMIT_X && crossed.current !== (v < 0 ? "l" : "r")) {
      crossed.current = v < 0 ? "l" : "r";
      haptic(8);
    }
  });

  const scale = 1 - offsetIndex * 0.04;
  const translateY = offsetIndex * 10;
  const opacity = 1 - offsetIndex * 0.12;

  function handleDragEnd(_, info) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const { offset, velocity } = info;
    let dir = null;
    if (offset.y < -h * COMMIT_Y || velocity.y < -VEL) dir = "up";
    else if (offset.x > w * COMMIT_X || velocity.x > VEL) dir = "right";
    else if (offset.x < -w * COMMIT_X || velocity.x < -VEL) dir = "left";
    crossed.current = null;
    if (dir) {
      if (directionRef) directionRef.current = dir;
      onCommit(dir);
    }
  }

  const allTags = cardTags(project.u_tags);
  const tags = allTags.slice(0, 3);
  const extra = allTags.length - tags.length;

  return (
    <motion.div
      className="absolute inset-0 touch-none select-none"
      custom={directionRef ? directionRef.current : null}
      style={{
        x: isTop ? x : 0,
        y: isTop ? y : translateY,
        rotate: isTop ? rotate : 0,
      }}
      initial={isTop ? { scale: 0.96, y: 10 } : false}
      animate={{ scale, y: isTop ? 0 : translateY, opacity }}
      variants={{ exit: (dir) => exitVariant(dir) }}
      exit="exit"
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      drag={isTop && !reduced}
      dragSnapToOrigin
      dragElastic={0.5}
      onDragEnd={handleDragEnd}
      data-testid={isTop ? "swipe-card-top" : "swipe-card"}
      aria-hidden={!isTop}
    >
      <div className="flex h-full w-full flex-col overflow-hidden rounded-card bg-white" style={{ boxShadow: "0 24px 50px -18px rgba(6,15,28,0.80), 0 8px 20px -8px rgba(6,15,28,0.45)" }}>
        <div className="relative aspect-square w-full">
          <HeroArt project={project} variant="card" className="h-full w-full" />
          <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
            <TrackBadge track={project.track} />
            <WinnerBadge slug={project.slug} />
            {project.backed && SUPPORT_ENABLED && <span className="inline-flex items-center gap-1 rounded-pill bg-candy-500/90 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur">Backed 💛</span>}
          </div>
          <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
            <StageChip stage={project.stage} />
            {project.video_url && <span className="inline-flex items-center gap-1 rounded-pill bg-navy-900/75 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur"><PlayCircle size={12} /> Video</span>}
          </div>
          {showHint && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-pill bg-navy-900/70 px-3 py-1 text-[12px] font-semibold text-white backdrop-blur">
              ⬆ Swipe up for the full story
            </div>
          )}
          {isTop && !reduced && (
            <>
              <motion.div style={{ opacity: leftOpacity }} className="pointer-events-none absolute right-4 top-6 -rotate-12 rounded-md border-4 border-coral-500 px-3 py-1 text-[22px] font-extrabold text-coral-500">NOT FOR ME</motion.div>
              <motion.div style={{ opacity: rightOpacity }} className="pointer-events-none absolute left-4 top-6 rotate-12 rounded-md border-4 border-mint-500 px-3 py-1 text-[22px] font-extrabold text-mint-500">{SUPPORT_ENABLED ? "SUPPORT 💛" : "SAVE ⭐"}</motion.div>
              <motion.div style={{ opacity: upOpacity }} className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-md border-4 border-sky-500 px-3 py-1 text-[20px] font-extrabold text-sky-500">TELL ME MORE ⬆</motion.div>
            </>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2 p-5">
          <h2 className="font-display text-[26px] font-bold leading-tight text-navy-900 line-clamp-2">{project.project}</h2>
          <div className="flex items-center gap-2">
            {project.team_members?.length > 0 && <AvatarStack members={project.team_members} size={28} max={4} />}
            <p className="truncate text-[14px] font-medium text-ink-600">by {project.team} · {project.stage}</p>
          </div>
          <p className="text-[16px] leading-snug text-ink-900 line-clamp-3">{project.one_liner}</p>
          <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
            {tags.map((t) => <TagChip key={t} tag={t} size="sm" />)}
            {extra > 0 && <span className="rounded-pill bg-navy-100 px-2 py-0.5 text-[11px] font-bold text-navy-900">+{extra}</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
